import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

export const TermsPage = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    document.title = 'Terms & Conditions — Custom Group';
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-[#f5f5f5]">
      {/* Nav */}
      <nav className="bg-[#0a0a0a] border-b border-[#222222] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center">
              <img src="/logo.svg" alt="Custom Group.Tech" className="h-9 w-auto" />
            </Link>
            <div className="hidden md:flex items-center gap-10 text-sm font-medium text-[#888888]">
              <Link to="/blog" className="hover:text-[#f5f5f5] transition-colors">Insights</Link>
              <Link to="/#approach" className="hover:text-[#f5f5f5] transition-colors">Our Approach</Link>
              <Link to="/#access" className="hover:text-[#f5f5f5] transition-colors">Advisory Access</Link>
              <Link
                to="/apply"
                className="px-5 py-2 text-sm font-semibold text-white transition-all hover:opacity-90"
                style={{ background: 'linear-gradient(135deg, #E8651A 0%, #F5922B 100%)' }}
              >
                Apply for Access
              </Link>
            </div>
            <button
              className="md:hidden p-2 text-[#888888] hover:text-[#f5f5f5]"
              onClick={() => setMobileMenuOpen((v) => !v)}
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" />
                </svg>
              ) : (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              )}
            </button>
          </div>
          {mobileMenuOpen && (
            <div className="md:hidden border-t border-[#222222] py-6 flex flex-col gap-4 text-sm font-medium">
              <Link to="/blog" className="text-[#888888] hover:text-[#f5f5f5]" onClick={() => setMobileMenuOpen(false)}>Insights</Link>
              <Link to="/#approach" className="text-[#888888] hover:text-[#f5f5f5]" onClick={() => setMobileMenuOpen(false)}>Our Approach</Link>
              <Link to="/#access" className="text-[#888888] hover:text-[#f5f5f5]" onClick={() => setMobileMenuOpen(false)}>Advisory Access</Link>
              <Link
                to="/apply"
                className="px-5 py-3 text-sm font-semibold text-center text-white"
                style={{ background: 'linear-gradient(135deg, #E8651A 0%, #F5922B 100%)' }}
                onClick={() => setMobileMenuOpen(false)}
              >
                Apply for Access
              </Link>
            </div>
          )}
        </div>
      </nav>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-16">
        <div className="flex items-center gap-3 mb-8">
          <div style={{ width: 28, height: 3, background: 'linear-gradient(90deg, #E8651A, #F5922B)' }} />
          <span className="text-xs uppercase tracking-widest text-[#E8651A] font-semibold">Legal</span>
        </div>
        <h1 className="text-4xl sm:text-5xl font-bold mb-4 tracking-tight">Terms & Conditions</h1>
        <p className="text-[#555555] text-sm mb-12">Last updated: 20 February 2026</p>

        <div className="article-body">
          <h2>1. Engagement Terms</h2>
          <p>
            All advisory engagements with Custom Group are governed by an Engagement Agreement ("Agreement") executed prior to commencement of work. 
            These Terms & Conditions supplement the Agreement and apply to all engagements unless explicitly modified in writing.
          </p>

          <h2>2. ROI Guarantee</h2>
          <p>
            Custom Group guarantees measurable operational improvement as defined in the Engagement Agreement. If Client implements 
            the Advisory Deliverables within six (6) months of Engagement Completion Date, adheres to the Implementation Specifications, 
            and completes the Internal Change Requirements, and does not achieve the Success Criteria within ninety (90) days of Go-Live Date, 
            Custom Group will refund 100% of the Advisory Fee.
          </p>

          <h3>2.1 Guarantee Conditions</h3>
          <p>The ROI Guarantee is void if:</p>
          <ul>
            <li>Implementation is not completed within six (6) months of Engagement Completion Date</li>
            <li>Client materially deviates from Implementation Specifications provided by Custom Group</li>
            <li>Client does not complete Internal Change Requirements (including but not limited to: team training, workflow redesign, process documentation)</li>
            <li>Client refuses reasonable audit of measurement methodology</li>
            <li>External factors beyond Custom Group's control prevent implementation (e.g., organizational restructuring, budget cuts, regulatory changes)</li>
          </ul>

          <h3>2.2 Measurement Criteria</h3>
          <p>"Measurable operational improvement" is defined as documented evidence of one or more of the following:</p>
          <ul>
            <li><strong>Time savings:</strong> ≥10 hours/week reduction in manual process time across the organization</li>
            <li><strong>Cost reduction:</strong> ≥R50,000/year (or USD $3,000/year) reduction in operational costs</li>
            <li><strong>Revenue increase:</strong> ≥R100,000/year (or USD $6,000/year) increase in revenue capacity</li>
          </ul>
          <p>
            Measurement methodology and baseline metrics are established during the Discovery Phase and documented in the Engagement Agreement. 
            Custom Group reserves the right to audit measurement processes to ensure accuracy.
          </p>

          <h2>3. Scope & Deliverables</h2>
          <p>
            Each engagement defines specific deliverables in the Engagement Agreement. Advisory deliverables may include (but are not limited to): 
            strategic recommendations, implementation roadmaps, ROI models, technical specifications, and board-level reports. 
            Custom Group is not responsible for implementation unless explicitly contracted as part of the engagement scope.
          </p>

          <h2>4. Client Responsibilities</h2>
          <p>Successful engagement outcomes require Client to:</p>
          <ul>
            <li>Provide timely access to necessary personnel, systems, and data</li>
            <li>Complete agreed-upon internal process changes</li>
            <li>Adhere to implementation timelines and specifications</li>
            <li>Assign internal ownership for deployment and change management</li>
            <li>Provide accurate baseline data for measurement purposes</li>
          </ul>

          <h2>5. Confidentiality</h2>
          <p>
            Custom Group treats all client information as confidential and will not disclose client identity, engagement details, or proprietary information 
            without explicit written consent. Case studies and testimonials require prior written approval.
          </p>

          <h2>6. Payment Terms</h2>
          <p>
            Unless otherwise specified in the Engagement Agreement, advisory fees are structured as follows:
          </p>
          <ul>
            <li><strong>AI Strategy Intensive:</strong> 100% due upon engagement commencement</li>
            <li><strong>AI ROI Mandate:</strong> 50% due upon engagement commencement, 50% due upon delivery of final deliverables</li>
            <li><strong>AI Transformation Advisory:</strong> Milestone-based payments as defined in Engagement Agreement</li>
          </ul>
          <p>
            Payments are due within 14 days of invoice date. Overdue payments accrue interest at 2% per month. Custom Group reserves the right 
            to suspend work on overdue accounts.
          </p>

          <h2>7. Limitation of Liability</h2>
          <p>
            Custom Group's total liability for any claim arising from an engagement shall not exceed the total advisory fees paid for that engagement. 
            Custom Group is not liable for indirect, consequential, or punitive damages, including but not limited to lost profits, business interruption, 
            or reputational harm.
          </p>

          <h2>8. Intellectual Property</h2>
          <p>
            All deliverables created specifically for Client become Client's property upon full payment. Custom Group retains ownership of methodologies, 
            frameworks, and tools used in the delivery of advisory services. Client may not resell, license, or distribute Custom Group's methodologies 
            without written consent.
          </p>

          <h2>9. Termination</h2>
          <p>
            Either party may terminate an engagement with 30 days' written notice. Upon termination, Client is responsible for payment of all work 
            completed to date. The ROI Guarantee does not apply to terminated engagements.
          </p>

          <h2>10. Governing Law</h2>
          <p>
            These Terms & Conditions and all Engagement Agreements are governed by the laws of the Republic of South Africa. 
            Any disputes shall be resolved through arbitration in Durban, KwaZulu-Natal.
          </p>

          <h2>11. Amendments</h2>
          <p>
            Custom Group reserves the right to update these Terms & Conditions at any time. Changes apply to engagements commenced after the date of amendment. 
            Existing engagements remain governed by the terms in effect at the time of Agreement execution.
          </p>

          <h2>12. Contact</h2>
          <p>
            For questions regarding these Terms & Conditions or to request clarification on engagement terms, contact:
          </p>
          <p className="text-[#f5f5f5]">
            <strong>Custom Group</strong><br />
            Ballito, KwaZulu-Natal, South Africa<br />
            Email: <a href="mailto:douglas@custom-group.tech" className="text-[#E8651A] hover:underline">douglas@custom-group.tech</a>
          </p>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-[#222222] py-16 px-6 lg:px-8">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-start gap-8">
          <div>
            <img src="/logo.svg" alt="Custom Group.Tech" className="h-10 w-auto mb-3" />
            <p className="text-xs text-[#888888] italic mb-1">We think the future, your future</p>
            <p className="text-xs text-[#555555]">Ballito, South Africa · © 2026 Custom Group.Tech. All rights reserved.</p>
          </div>
          <div className="flex flex-wrap gap-8 text-sm text-[#888888]">
            <Link to="/blog" className="hover:text-[#f5f5f5] transition-colors">Insights</Link>
            <Link to="/founder" className="hover:text-[#f5f5f5] transition-colors">About</Link>
            <Link to="/apply" className="hover:text-[#f5f5f5] transition-colors">Apply</Link>
            <Link to="/terms" className="hover:text-[#f5f5f5] transition-colors">Terms</Link>
            <a
              href="https://www.linkedin.com/in/douglasbothma/"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-[#f5f5f5] transition-colors"
            >
              LinkedIn
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
};
